import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PhotoHoldCanvas } from '../../../components/climbs/photo-editor/PhotoHoldCanvas';
import { PhotoOverlayTools } from '../../../components/climbs/photo-editor/PhotoOverlayTools';
import { CircuitOverlayCorner } from '../../../components/climbs/CircuitOverlayButtons';
import type { PhotoHoldsController } from '../../../components/climbs/photo-editor/usePhotoHolds';
import { HoldRolesLegendDialog } from '../../../components/climbs/HoldRolesLegendDialog';
import { PhotoHoldBar } from '../../../components/climbs/climb-editor/PhotoHoldBar';
import { MissingHint } from '../../../components/climbs/climb-editor/MissingHint';
import { DRAWING_SURFACE_ATTRIBUTE } from '../../../components/climbs/climb-editor/useClearSelectionOutside';
import {
  PHOTO_HOLD_ROLE_CYCLE,
  type PhotoHoldRole,
} from '../../../components/climbs/photo-hold-roles';
import type { BoardHand } from '../../../types/device';
import { ScanEditorLayout, type ScanEditorChrome } from './ScanEditorLayout';

interface ScanHoldEditorProps {
  imageUrl: string;
  /**
   * The circles and the gestures, made by the page rather than here. The page
   * needs this one at save time — its zoom says which part of the photo is on
   * screen, and that is the crop — so a controller made in here would be out of
   * its reach.
   */
  editor: PhotoHoldsController;
  /**
   * Which of the required roles the drawing is still missing — a start, a hold
   * to climb on, a top. Written on the photo, which is the only place any of
   * them can be answered.
   */
  missingRoles: PhotoHoldRole[];
  /**
   * Circuit mode: whether this climb's holds are a sequence rather than a set.
   * Switched from the corner of the photo, because the order is drawn on the
   * photo — the circuit itself is read back in the controls column (see
   * `ScanEditorChrome.circuit`).
   */
  circuit: boolean;
  onCircuitChange: (on: boolean) => void;
  /**
   * The hand(s) each circle is taken with, on a circuit that forces them —
   * painted onto its own side of the circle. The page reads them off the run
   * (see `PhotoHoldCanvas#handsOf`).
   */
  handsOf?: (index: number) => BoardHand[];
  /** The page's half of the two columns these controls live in. */
  chrome: ScanEditorChrome;
}

/**
 * Circles the holds of a climb on its scan photo, in the two columns of the
 * scans page.
 *
 * Everything is on the picture, exactly as it is on the member app's climb
 * editor (see `PhotoClimbSurface`, which this is the panel's copy of): what acts
 * on the circle in hand is docked along the bottom of the picture
 * (`PhotoHoldBar`), what acts on the drawing as a whole is in the corner of the
 * picture it acts on, and what the climb still wants is written across the top
 * of it. The controls column is left to the form — a row of icons above a form
 * was one more place to look, and the one place the setter is already looking is
 * the wall.
 *
 * **The bar is docked rather than standing at the hold** (it used to be
 * `HoldPopover`). A panel at the mark covers the wall the setter is about to
 * press next and moves on every press; a bar with a fixed place is one the thumb
 * finds without looking. The member app settled this first and the panel now
 * matches it. What goes with the popover is its size slider — a circle is still
 * resized by its own handles, and a traced shape was never meant to be resized
 * at all.
 *
 * The editor itself is shared with the member app — see
 * `components/climbs/photo-editor` — so this is the panel's layout and its copy
 * and nothing else. The one thing the page asks for of its own is what a press
 * on the circle already chosen does: here it walks the **role**, because a
 * scan's analysis gets the wall's colour wrong more often than it gets a hold's
 * size wrong, and correcting that is most of the work on this page.
 *
 * It opens on what the scan's analysis found — a circle per detected hold — so
 * the work is correcting a climb rather than drawing one.
 */
export function ScanHoldEditor({
  imageUrl,
  editor,
  missingRoles,
  circuit,
  onCircuitChange,
  handsOf,
  chrome,
}: ScanHoldEditorProps) {
  const { t } = useTranslation();
  const [showHelp, setShowHelp] = useState(false);

  const canvas = (
    // The marks and the panel that acts on them are one surface: a press inside
    // it carries on with the circle in hand, and a press anywhere else — the
    // form, the list of climbs — is the setter having finished with it.
    <div className="relative" {...{ [DRAWING_SURFACE_ATTRIBUTE]: '' }}>
      <PhotoHoldCanvas
        editor={editor}
        imageUrl={imageUrl}
        testId="scan-hold-frame"
        className="rounded-md border"
        holdLabel={({ role, x, y }) =>
          t('gymAdmin.scans.holdAt', { role: t(`climbs.holdRoles.${role}.label`), x, y })
        }
        resetZoomLabel={t('gymAdmin.scans.resetZoom')}
        handsOf={handsOf}
        // The wall drained of its colour, and this climb's holds lit on it.
        // On a scanned wall the holds are already found, so the work is saying
        // which plastic is this climb — and that reads at a glance here in a
        // way a ring on a busy photograph never did.
        spotlight
      />

      {/* Making the climb a circuit, top-left of the picture — the corner the
          member app keeps it in, where it sits beside the button that changes the
          photo. Here there is no photo to change (a scan's picture is the wall
          that was scanned) and nothing to open: the circuit is read back in the
          controls column. */}
      <CircuitOverlayCorner circuit={{ on: circuit, onToggle: onCircuitChange }} />

      <PhotoOverlayTools
        undoLabel={t('gymAdmin.scans.undoHold')}
        onUndo={editor.undo}
        canUndo={editor.canUndo}
        clearLabel={t('gymAdmin.scans.clearHolds')}
        onClear={editor.clearAll}
        canClear={editor.holds.length > 0}
        helpLabel={t('climbs.holdRoles.legend.open')}
        onHelp={() => setShowHelp(true)}
      />

      <PhotoHoldBar editor={editor} />

      <MissingHint items={missingRoles.map((role) => t(`climbs.holdRoles.${role}.label`))} />

      <HoldRolesLegendDialog
        open={showHelp}
        onOpenChange={setShowHelp}
        roles={PHOTO_HOLD_ROLE_CYCLE}
        hint={t('gymAdmin.scans.holdHint')}
      />
    </div>
  );

  return <ScanEditorLayout canvas={canvas} {...chrome} />;
}
